/**
 * A Drive UI integráció `state` query paraméterének értelmezése.
 *
 * Megnyitás:
 *   {"ids":["1AbC"],"action":"open","userId":"108…","resourceKeys":{"1AbC":"key"}}
 * Új fájl:
 *   {"action":"create","folderId":"0BxY","userId":"108…"}
 *
 * A Drive régebbi/újabb kliensei `folderId` és `folderIds` alakot is küldhetnek,
 * ezért mindkettőt kezeljük.
 */

export interface OpenState {
  action: 'open';
  fileId: string;
  userId?: string;
  resourceKey?: string;
}

export interface CreateState {
  action: 'create';
  folderId?: string;
  /** Link-megosztott mappánál a Drive resource key-t is küld. */
  folderResourceKey?: string;
  userId?: string;
}

export type DriveState = OpenState | CreateState;

interface RawState {
  action?: string;
  ids?: unknown;
  exportIds?: unknown;
  folderId?: unknown;
  folderIds?: unknown;
  folderResourceKey?: unknown;
  userId?: unknown;
  resourceKeys?: Record<string, string>;
}

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string' && value) return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
}

/** Nyers state string → tipizált állapot. Ismeretlen/hibás bemenetre null. */
export function parseState(raw: string | null): DriveState | null {
  if (!raw) return null;
  let data: RawState;
  try {
    data = JSON.parse(raw) as RawState;
  } catch {
    try {
      data = JSON.parse(decodeURIComponent(raw)) as RawState;
    } catch {
      return null;
    }
  }

  const userId = typeof data.userId === 'string' ? data.userId : undefined;

  if (data.action === 'create') {
    return {
      action: 'create',
      folderId: firstString(data.folderId ?? data.folderIds),
      folderResourceKey: firstString(data.folderResourceKey),
      userId,
    };
  }

  if (data.action === 'open') {
    // Natív Google fájloknál exportIds érkezik – ezt nem támogatjuk (nem markdown).
    const fileId = firstString(data.ids);
    if (!fileId) return null;
    return {
      action: 'open',
      fileId,
      userId,
      resourceKey: data.resourceKeys?.[fileId],
    };
  }

  return null;
}

/** A böngésző címsorából olvassa ki a state-et. */
export function readStateFromLocation(search = window.location.search): DriveState | null {
  const raw = new URLSearchParams(search).get('state');
  const parsed = parseState(raw);
  // Hibakereséshez: a Drive által küldött nyers állapot. Fájltartalmat nem tartalmaz,
  // csak azonosítókat — a konzolban bármikor visszanézhető.
  if (raw) {
    // eslint-disable-next-line no-console
    console.info('[Bitsquad MD] Drive state:', raw, '→', parsed);
  }
  return parsed;
}
